import { useCallback, useEffect, useMemo, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/administration/masters/categories/Header';
import { Box, Button, IconButton, MenuItem, Paper, Select, Tab, Tabs } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/administration/masters/categories/CreateDialog';
import UpdateDialog from '../../../../components/administration/masters/categories/UpdateDialog';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import { Category, ContainerCategory, ProductCategory } from '../../../../../core/interfaces/category.interface';
import { CREATE_CATEGORY_DIALOG, CREATE_CATEGORY_WITH_LIMIT_DIALOG, UPDATE_CATEGORY_DIALOG, UPDATE_CATEGORY_WITH_LIMIT_DIALOG } from '../../../../../core/constants/dialog';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import TreeView, { flattenTree } from "react-accessible-treeview";
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CreateDialogWithLimit from '../../../../components/administration/masters/categories/CreateDialogWithLimit';
import UpdateDialogWithLimit from '../../../../components/administration/masters/categories/UpdateDialogWithLimit';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Categories = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { productTypes, containerTypes, productCategories, containerCategories, setCategory, getCategories, getTypes, clearError, putCategory } = useMasterCore();
  
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [parentId, setParentId] = useState<number | null>(null);
  const [type, setType] = useState(0);
  const [tab, setTab] = useState(0);
  const [tType, setTType] = useState<tType>('product');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [expandedContainerCategories, setExpandedContainerCategories] = useState<Set<number>>(new Set());
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [containerSortModel, setContainerSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setType(0);
    setTab(newValue);
  };

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.writeMaster
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      // Cargar tipos necesarios para el filtro (producto y contenedor)
      getTypes(companyId, 'product');
      getTypes(companyId, 'container');
      // Cargar categorías
      getCategories(companyId, 'product');
      getCategories(companyId, 'container');
    }
  }, [companyId, getCategories, getTypes]);

  const selectCategory = useCallback((id: number, type: tType) => {
    let category;
    
    if (type === 'product') {
      // Función recursiva para buscar en cualquier nivel del árbol
      const findCategoryRecursive = (categories: Category[]): Category | null => {
        for (const cat of categories) {
          if (cat.id === id) {
            return cat;
          }
          
          const productCategory = cat as ProductCategory;
          if (productCategory.child_categories && productCategory.child_categories.length > 0) {
            const found = findCategoryRecursive(productCategory.child_categories);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      
      category = findCategoryRecursive(productCategories);
    } else {
      category = containerCategories.find((cat) => cat.id === id);
    }

    if (category) {
      setCategory(category);
      if (tab === 0) {
        setTType('product');
        openDialog(UPDATE_CATEGORY_DIALOG);
      } else {
        setTType('container');
        openDialog(UPDATE_CATEGORY_WITH_LIMIT_DIALOG);
      }
      clearError();
    }
  }, [clearError, containerCategories, openDialog, productCategories, setCategory, tab]);

  const openCreateDialog = useCallback(() => {
    if (tab === 0) {
      setTType('product');
      openDialog(CREATE_CATEGORY_DIALOG);
    } else {
      setTType('container');
      openDialog(CREATE_CATEGORY_WITH_LIMIT_DIALOG);
    }
    clearError();
  }, [clearError, openDialog, tab]);

  const getRealId = useCallback((nodeId: any) => {
    return Number(String(nodeId).replace('parent-', '').replace('child-', ''));
  }, []);

  const openCreateDialogWithChildren = useCallback((element: any) => {
    if (element.id) {
      setParentId(getRealId(element.id));
      if (tab === 0) {
        setTType('product');
        openDialog(CREATE_CATEGORY_DIALOG);
      } else {
        setTType('container');
        openDialog(CREATE_CATEGORY_WITH_LIMIT_DIALOG);
      }
    } else {
      setParentId(null);
    }
  }, [openDialog, tab, getRealId]);

  const filteredProductCategories = useMemo(() => {
    // El backend ya envía solo las categorías padre, así que solo filtramos por tipo si es necesario
    if (type === 0) {
      return productCategories;
    }
    // Si hay filtro, mostrar solo las categorías padre del tipo seleccionado
    return productCategories.filter((category) => (category as ProductCategory).product_type_id === type);
  }, [productCategories, type]);

  // Función para aplanar las categorías recursivamente para la tabla
  const flattenCategoriesForTable = useCallback((categories: ProductCategory[], level: number = 0, showChildren: boolean = true, parentId: number | null = null): any[] => {
    const result: any[] = [];
    
    categories.forEach((category) => {
      // Obtener el nombre del tipo de producto
      const productType = productTypes.find(t => t.id === category.product_type_id);
      const productTypeName = productType ? (productType.name || productType.description) : 'N/A';
      
      const hasChildren = category.child_categories && category.child_categories.length > 0;
      const isExpanded = expandedCategories.has(category.id);
      const childCategories = category.child_categories;
      
      result.push({
        id: category.id,
        name: category.name,
        description: category.description,
        product_type_id: category.product_type_id,
        product_type_name: productTypeName,
        parent_id: category.parent_id || parentId,
        level: level,
        hasChildren: hasChildren,
        isExpanded: isExpanded,
        parentKey: parentId, // Clave para agrupar hijos con su padre
      });
      
      // Agregar hijos recursivamente solo si está expandido y showChildren es true
      if (hasChildren && isExpanded && showChildren && childCategories) {
        const sortedChildren = childCategories
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        result.push(...flattenCategoriesForTable(sortedChildren, level + 1, true, category.id));
      }
    });
    
    return result;
  }, [productTypes, expandedCategories]);

  const buildProductCategoryTree = useCallback((categories: Category[]) => {
    // Función recursiva para construir el árbol con múltiples niveles
    const buildNode = (category: ProductCategory, level: number = 0): any => {
      const nodeId = level === 0 ? `parent-${category.id}` : `child-${category.id}`;
      
      // Obtener el nombre del tipo de producto
      const productType = productTypes.find(t => t.id === category.product_type_id);
      const productTypeName = productType ? (productType.name || productType.description) : 'N/A';
      
      // Ordenar los hijos alfabéticamente antes de mapearlos
      const sortedChildren = (category.child_categories || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(child => buildNode(child, level + 1));
      
      return {
        id: nodeId,
        name: category.name,
        productTypeName: productTypeName,
        categoryId: category.id,
        children: sortedChildren
      };
    };

    // Ordenar las categorías padre alfabéticamente
    const sortedCategories = categories
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(category => {
        const productCategory = category as ProductCategory;
        return buildNode(productCategory);
      });

    return sortedCategories;
  }, [productTypes]);

  const dataProductCategories = flattenTree({
    name: "",
    children: buildProductCategoryTree(filteredProductCategories),
  });

  // Datos aplanados para la tabla (sin ordenamiento, se ordenará después)
  const flattenedProductCategories = useMemo(() => {
    // Solo procesar categorías raíz (sin parent_id)
    const rootCategories = (filteredProductCategories as ProductCategory[])
      .filter(cat => !cat.parent_id);
    return flattenCategoriesForTable(rootCategories);
  }, [filteredProductCategories, flattenCategoriesForTable]);

  // Procesar datos ordenados respetando la jerarquía
  // Solo ordena las categorías padre, los hijos siempre se mantienen alfabéticamente bajo su padre
  const sortedProductCategories = useMemo(() => {
    if (!sortModel || sortModel.length === 0) {
      return flattenedProductCategories;
    }

    const sortField = sortModel[0].field;
    const sortDirection = sortModel[0].sort;

    // Crear un mapa de todos los items por ID para acceso rápido
    const itemsById: { [key: number]: any } = {};
    flattenedProductCategories.forEach((item) => {
      itemsById[item.id] = item;
    });

    // Separar padres (level 0) de hijos (level > 0)
    const parentItems: any[] = [];
    const childrenByParent: { [key: number]: any[] } = {};

    flattenedProductCategories.forEach((item) => {
      if (item.level === 0) {
        // Es una categoría padre
        parentItems.push(item);
      } else {
        // Es un hijo, agruparlo por su padre usando parentKey
        const parentKey = item.parentKey;
        if (parentKey !== null && parentKey !== undefined) {
          if (!childrenByParent[parentKey]) {
            childrenByParent[parentKey] = [];
          }
          childrenByParent[parentKey].push(item);
        }
      }
    });

    // Ordenar solo las categorías padre por el campo seleccionado
    const sortedParents = [...parentItems].sort((a, b) => {
      let aVal: string;
      let bVal: string;
      
      if (sortField === 'name') {
        aVal = String(a.name || '').toLowerCase();
        bVal = String(b.name || '').toLowerCase();
      } else if (sortField === 'product_type_name') {
        aVal = String(a.product_type_name || '').toLowerCase();
        bVal = String(b.product_type_name || '').toLowerCase();
      } else {
        aVal = String(a[sortField] || '').toLowerCase();
        bVal = String(b[sortField] || '').toLowerCase();
      }
      
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Función recursiva para agregar hijos y sus descendientes
    const addChildrenRecursively = (parentId: number, result: any[]) => {
      const children = childrenByParent[parentId];
      if (children && children.length > 0) {
        // Ordenar hijos alfabéticamente por nombre
        const sortedChildren = [...children].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        
        sortedChildren.forEach((child) => {
          result.push(child);
          // Si el hijo también tiene hijos expandidos, agregarlos recursivamente
          if (child.isExpanded) {
            addChildrenRecursively(child.id, result);
          }
        });
      }
    };

    // Reconstruir el array: padres ordenados + hijos siempre alfabéticos bajo su padre
    const result: any[] = [];
    sortedParents.forEach((parent) => {
      result.push(parent);
      // Si el padre está expandido, agregar sus hijos recursivamente
      if (parent.isExpanded) {
        addChildrenRecursively(parent.id, result);
      }
    });

    return result;
  }, [flattenedProductCategories, sortModel]);


  // Función para toggle expand/collapse
  const toggleExpand = useCallback((categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Columnas para la tabla de categorías de productos
  const productCategoryColumns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <div style={{ paddingLeft: `${params.row.level * 20}px` }} className="flex items-center">
          <div style={{ width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.row.hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(params.row.id);
                }}
                sx={{ padding: '4px' }}
              >
                {params.row.isExpanded ? (
                  <ArrowDropDownIcon fontSize="small" className="text-gray-600" />
                ) : (
                  <ArrowRightIcon fontSize="small" className="text-gray-600" />
                )}
              </IconButton>
            ) : (
              <IconButton
                size="small"
                disabled
                sx={{ padding: '4px', cursor: 'default', '&.Mui-disabled': { opacity: 0.3 } }}
              >
                <FiberManualRecordIcon sx={{ fontSize: '8px', color: '#9ca3af' }} />
              </IconButton>
            )}
          </div>
          <span style={{ lineHeight: '1.5' }}>{params.row.name}</span>
        </div>
      ),
    },
    {
      field: 'product_type_name',
      headerName: 'Tipo de Producto',
      flex: 1,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'text-end',
      sortable: false,
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const categoryId = params.row.id;
        const category = filteredProductCategories.find(cat => {
          const findRecursive = (cats: ProductCategory[]): ProductCategory | undefined => {
            for (const c of cats) {
              if (c.id === categoryId) return c;
              if (c.child_categories) {
                const found = findRecursive(c.child_categories);
                if (found) return found;
              }
            }
            return undefined;
          };
          return findRecursive([cat as ProductCategory]);
        }) as ProductCategory | undefined;

        return (
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setParentId(categoryId);
                setTType('product');
                openDialog(CREATE_CATEGORY_DIALOG);
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                selectCategory(categoryId, 'product');
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
  ], [filteredProductCategories, openDialog, selectCategory, toggleExpand]);

  const filteredContainerCategories = useMemo(() => {
    // Si type es 0 (no hay filtro), mostrar todas las categorías
    if (type === 0) {
      return containerCategories;
    }
    // Si hay filtro, mostrar solo las categorías del tipo seleccionado
    return containerCategories.filter((category) => (category as ContainerCategory).container_type_id === type);
  }, [containerCategories, type]);

  // Función para aplanar las categorías de contenedor recursivamente para la tabla
  const flattenContainerCategoriesForTable = useCallback((categories: ContainerCategory[], level: number = 0, showChildren: boolean = true, parentId: number | null = null): any[] => {
    const result: any[] = [];
    
    categories.forEach((category) => {
      const acronym = category.acronym;
      const displayName = acronym ? `(${acronym}) ${category.name}` : category.name;
      const hasChildren = category.child_categories && category.child_categories.length > 0;
      const isExpanded = expandedContainerCategories.has(category.id);
      const childCategories = category.child_categories;
      
      // Obtener el nombre del tipo de contenedor
      const containerType = containerTypes.find(t => t.id === category.container_type_id);
      const containerTypeName = containerType ? (containerType.name || containerType.description) : 'N/A';
      
      result.push({
        id: category.id,
        name: displayName,
        description: category.description,
        container_type_id: category.container_type_id,
        container_type_name: containerTypeName,
        acronym: acronym,
        parent_id: category.parent_id || parentId,
        level: level,
        hasChildren: hasChildren,
        isExpanded: isExpanded,
        parentKey: parentId, // Clave para agrupar hijos con su padre
      });
      
      // Agregar hijos recursivamente solo si está expandido y showChildren es true
      if (hasChildren && isExpanded && showChildren && childCategories) {
        const sortedChildren = childCategories
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        result.push(...flattenContainerCategoriesForTable(sortedChildren, level + 1, true, category.id));
      }
    });
    
    return result;
  }, [expandedContainerCategories, containerTypes]);

  // Función para toggle expand/collapse de categorías de contenedor
  const toggleContainerExpand = useCallback((categoryId: number) => {
    setExpandedContainerCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Datos aplanados para la tabla de contenedores (sin ordenamiento, se ordenará después)
  const flattenedContainerCategories = useMemo(() => {
    // Solo procesar categorías raíz (sin parent_id)
    const rootCategories = (filteredContainerCategories as ContainerCategory[])
      .filter(cat => !cat.parent_id);
    return flattenContainerCategoriesForTable(rootCategories);
  }, [filteredContainerCategories, flattenContainerCategoriesForTable]);

  // Procesar datos ordenados respetando la jerarquía para contenedores
  // Solo ordena las categorías padre, los hijos siempre se mantienen alfabéticamente bajo su padre
  const sortedContainerCategories = useMemo(() => {
    if (!containerSortModel || containerSortModel.length === 0) {
      return flattenedContainerCategories;
    }

    const sortField = containerSortModel[0].field;
    const sortDirection = containerSortModel[0].sort;

    // Separar padres (level 0) de hijos (level > 0)
    const parentItems: any[] = [];
    const childrenByParent: { [key: number]: any[] } = {};

    flattenedContainerCategories.forEach((item) => {
      if (item.level === 0) {
        // Es una categoría padre
        parentItems.push(item);
      } else {
        // Es un hijo, agruparlo por su padre usando parentKey
        const parentKey = item.parentKey;
        if (parentKey !== null && parentKey !== undefined) {
          if (!childrenByParent[parentKey]) {
            childrenByParent[parentKey] = [];
          }
          childrenByParent[parentKey].push(item);
        }
      }
    });

    // Ordenar solo las categorías padre por el campo seleccionado
    const sortedParents = [...parentItems].sort((a, b) => {
      let aVal: string;
      let bVal: string;
      
      if (sortField === 'name') {
        aVal = String(a.name || '').toLowerCase();
        bVal = String(b.name || '').toLowerCase();
      } else if (sortField === 'container_type_name') {
        aVal = String(a.container_type_name || '').toLowerCase();
        bVal = String(b.container_type_name || '').toLowerCase();
      } else {
        aVal = String(a[sortField] || '').toLowerCase();
        bVal = String(b[sortField] || '').toLowerCase();
      }
      
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Función recursiva para agregar hijos y sus descendientes
    const addChildrenRecursively = (parentId: number, result: any[]) => {
      const children = childrenByParent[parentId];
      if (children && children.length > 0) {
        // Ordenar hijos alfabéticamente por nombre
        const sortedChildren = [...children].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        
        sortedChildren.forEach((child) => {
          result.push(child);
          // Si el hijo también tiene hijos expandidos, agregarlos recursivamente
          if (child.isExpanded) {
            addChildrenRecursively(child.id, result);
          }
        });
      }
    };

    // Reconstruir el array: padres ordenados + hijos siempre alfabéticos bajo su padre
    const result: any[] = [];
    sortedParents.forEach((parent) => {
      result.push(parent);
      // Si el padre está expandido, agregar sus hijos recursivamente
      if (parent.isExpanded) {
        addChildrenRecursively(parent.id, result);
      }
    });

    return result;
  }, [flattenedContainerCategories, containerSortModel]);

  // Columnas para la tabla de categorías de contenedor
  const containerCategoryColumns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const isExpanded = expandedContainerCategories.has(params.row.id);
        return (
          <div style={{ paddingLeft: `${params.row.level * 20}px` }} className="flex items-center">
            <div style={{ width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {params.row.hasChildren ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleContainerExpand(params.row.id);
                  }}
                  sx={{ padding: '4px' }}
                >
                  {isExpanded ? (
                    <ArrowDropDownIcon fontSize="small" className="text-gray-600" />
                  ) : (
                    <ArrowRightIcon fontSize="small" className="text-gray-600" />
                  )}
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  disabled
                  sx={{ padding: '4px', cursor: 'default', '&.Mui-disabled': { opacity: 0.3 } }}
                >
                  <FiberManualRecordIcon sx={{ fontSize: '8px', color: '#9ca3af' }} />
                </IconButton>
              )}
            </div>
            <span style={{ lineHeight: '1.5' }}>{params.row.name}</span>
          </div>
        );
      },
    },
    {
      field: 'container_type_name',
      headerName: 'Tipo de Contenedor',
      flex: 1,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'text-end',
      sortable: false,
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const categoryId = params.row.id;
        const category = filteredContainerCategories.find(cat => {
          const findRecursive = (cats: ContainerCategory[]): ContainerCategory | undefined => {
            for (const c of cats) {
              if (c.id === categoryId) return c;
              if (c.child_categories) {
                const found = findRecursive(c.child_categories);
                if (found) return found;
              }
            }
            return undefined;
          };
          return findRecursive([cat as ContainerCategory]);
        }) as ContainerCategory | undefined;

        return (
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setParentId(categoryId);
                setTType('container');
                openDialog(CREATE_CATEGORY_WITH_LIMIT_DIALOG);
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                selectCategory(categoryId, 'container');
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
  ], [filteredContainerCategories, openDialog, selectCategory, toggleContainerExpand, expandedContainerCategories]);

  const buildContainerCategoryTree = useCallback((categories: Category[]) => {
    // Container categories don't support hierarchies yet, so just return flat structure
    return categories.map(category => {
      const acronym = (category as ContainerCategory).acronym;
      const displayName = acronym ? `(${acronym}) ${category.name}` : category.name;
      return {
        id: category.id,
        name: displayName,
        children: []
      };
    });
  }, []);

  const dataContainerCategories = flattenTree({
    name: "",
    children: buildContainerCategoryTree(filteredContainerCategories),
  })

  const types = useMemo(() => {
    return tab === 0 ? productTypes : containerTypes; 
  }, [containerTypes, productTypes, tab]);

  const FolderIcon = ({ isOpen }: { isOpen: boolean }) =>
    isOpen ? (
      <ArrowRightIcon className="text-gray-600" />
    ) : (
      <ArrowDropDownIcon className="text-gray-600" />
    );


  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Categoría de producto" {...a11yProps(0)} />
                    <Tab label="Categoría de contenedor" {...a11yProps(1)} />
                  </Tabs>
                  <div className="flex items-center gap-4 mb-8">
                    <Select
                      className="min-w-[150px]"
                      placeholder="Tipo"
                      size="small"
                      disabled={!companyId}
                      value={type}
                      onChange={(ev) => setType(+ev.target.value)}
                    >
                      <MenuItem value={0}>
                        <em>Selecionar</em>
                      </MenuItem>
                      {types.map((type) => (
                        <MenuItem key={type.id} value={type.id}>{ type.name || type.description }</MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="contained"
                      color="secondary"
                      className="mr-8"
                      disabled={!companyId}
                      onClick={openCreateDialog}
                    >
                      <AddCircleOutlineOutlinedIcon />
                      <span className="mx-8">Crear</span>
                    </Button>
                  </div>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                  {!filteredProductCategories.length && (
                    <div className='text-center p-12 text-gray-400'>
                      {type === 0 ? 'No se encontraron categorías' : 'No se encontraron categorias para el tipo seleccionado'}
                    </div>
                  )}
                  {filteredProductCategories.length > 0 && (
                    <Paper>
                      <DataGrid
                        rows={sortedProductCategories}
                        columns={productCategoryColumns}
                        sx={{ border: 0 }}
                        sortModel={sortModel}
                        onSortModelChange={(model) => setSortModel(model)}
                        disableColumnFilter
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        sortingMode="server"
                        hideFooter
                        pagination={undefined}
                        getRowId={(row) => `row-${row.id}-${row.level}-${row.parentKey || 'root'}`}
                      />
                    </Paper>
                  )}
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                  {!filteredContainerCategories.length && (
                    <div className='text-center p-12 text-gray-400'>
                      {type === 0 ? 'No se encontraron categorías' : 'No se encontraron categorias para el tipo seleccionado'}
                    </div>
                  )}
                  {filteredContainerCategories.length > 0 && (
                    <Paper key={`container-grid-${expandedContainerCategories.size}`}>
                      <DataGrid
                        rows={sortedContainerCategories}
                        columns={containerCategoryColumns}
                        sortModel={containerSortModel}
                        onSortModelChange={(model) => setContainerSortModel(model)}
                        sx={{ border: 0 }}
                        disableColumnFilter
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        sortingMode="server"
                        hideFooter
                        pagination={undefined}
                        getRowId={(row) => `row-${row.id}-${row.level}-${row.parentKey || 'root'}`}
                      />
                    </Paper>
                  )}
                </CustomTabPanel>
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog type={tType} types={types} parent_id={parentId} onClose={() => setParentId(null)} />
      <UpdateDialog type={tType} types={types} parent_id={parentId} onClose={() => setParentId(null)} />
      <CreateDialogWithLimit type={tType} types={types} parent_id={parentId} onClose={() => setParentId(null)} />
      <UpdateDialogWithLimit type={tType} types={types} parent_id={parentId} onClose={() => setParentId(null)} />
    </>
  );
};

export default Categories;
